import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageBanner from "../components/PageBanner";
import CTASection from "../components/home/CTASection";
import Seo from "../components/Seo";
import useReveal from "../hooks/useReveal";
import usePublicData from "../hooks/usePublicData";
import { PROJECTS, IMAGES } from "../mock";
import { imageUrl } from "../lib/api";
import { ArrowUpRight } from "lucide-react";

const filters = ["All", "Kitchen", "Bathroom", "Basement", "Full Home"];

export default function Projects() {
  const { data: projects } = usePublicData("/projects", PROJECTS);
  useReveal([projects]);
  const [active, setActive] = useState("All");

  const norm = (p) => ({
    ...p,
    display: imageUrl(p.cover) || p.image,
    tag: p.tag || p.category,
    link: p.slug ? `/projects/${p.slug}` : null,
  });
  const list = projects
    .map(norm)
    .filter((p) => active === "All" || (p.tag || "").toLowerCase().includes(active.toLowerCase()));

  return (
    <>
      <Seo title="Our Projects" path="/projects" description="Explore recent kitchen, bathroom, basement and full-home renovation projects completed by Classic Interior Renovations in Edmonton." />
      <PageBanner crumb="Projects" title="Our recent work" subtitle="Take a look through our past projects and get those ideas flowing for your own space." image={IMAGES.living1} />

      <section className="py-24 bg-white">
        <div className="container-x">
          <div className="flex flex-wrap items-center gap-3 mb-12">
            {filters.map((f) => (
              <button key={f} onClick={() => setActive(f)} className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${active === f ? "bg-[#2f9fe6] text-white" : "bg-[#f2f5f8] text-[#0f2740] hover:bg-[#e6eef4]"}`}>{f}</button>
            ))}
          </div>

          {list.length === 0 ? (
            <div className="text-center py-20 text-[#6b7683]">No projects in this category yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {list.map((p, i) => {
                const Card = (
                  <div className="group cursor-pointer">
                    <div className="shape-soft relative mb-5">
                      <img src={p.display} alt={p.title} loading="lazy" decoding="async" className="w-full h-[300px] object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      {p.tag && <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 text-[#0f2740] text-xs font-semibold capitalize">{p.tag}</span>}
                      <span className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-[#2f9fe6] flex items-center justify-center text-white opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all"><ArrowUpRight className="w-5 h-5" /></span>
                    </div>
                    <h3 className="font-display text-xl font-semibold text-[#0f2740] mb-1 group-hover:text-[#2f9fe6] transition-colors">{p.title}</h3>
                    <p className="text-[#6b7683] text-sm mb-4">{p.location || p.area}{(p.location || p.area) ? ", Edmonton" : "Edmonton"}</p>
                    <div className="flex gap-6 text-sm border-t border-[#eef1f4] pt-4">
                      <div><div className="text-[#9aa4af] text-xs uppercase tracking-wide mb-1">Budget</div><div className="font-semibold text-[#0f2740]">{p.budget || "On request"}</div></div>
                      <div><div className="text-[#9aa4af] text-xs uppercase tracking-wide mb-1">Length</div><div className="font-semibold text-[#0f2740]">{p.length || "—"}</div></div>
                    </div>
                  </div>
                );
                return (
                  <div key={p.id || p.title} className={`reveal delay-${(i % 3) + 1}`}>
                    {p.link ? <Link to={p.link}>{Card}</Link> : Card}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
      <CTASection />
    </>
  );
}
